import { useQuery } from '@tanstack/react-query';

import { Skeleton } from '@/components/Skeleton/Skeleton';
import { type AppUsageParams, type AppUsageReport } from '@/features/reports/api';
import { DevicesCard, VersionsCard, WorthACallCard } from '@/features/reports/app/AppUsageCards';
import { firstNames, latestVersion, windowHint } from '@/features/reports/app/labels';
import { PeopleCard } from '@/features/reports/app/PeopleCard';
import { SessionLogCard } from '@/features/reports/app/SessionLogCard';
import { APP_USAGE_COPY as COPY } from '@/features/reports/copy';
import { ManagerFilter, ReportFailure, Tile, WindowControl } from '@/features/reports/parts';
import { appUsageQuery } from '@/features/reports/queries';
import { type AppUsageDays } from '@/features/reports/tabs';
import { useManagerName } from '@/features/reports/useManagerName';

/**
 * Reports › App usage (ALS-160): the app version log read like the Overview and Clients tabs
 * rather than as a table nobody reads without counting (Franka, 15 Sep 2026). One window, five
 * figures, three cards, one line per person, and the flat log folded under them for support. One
 * request for the window and the relationship manager, so the tab loads and fails as one; the
 * manager is the page's, the same choice as Clients'.
 */
export function AppUsagePanel({
    params,
    onDaysChange,
    onManagerChange,
}: {
    params: AppUsageParams;
    onDaysChange: (days: AppUsageDays) => void;
    onManagerChange: (rmId: number | undefined) => void;
}) {
    const report = useQuery(appUsageQuery(params));
    const manager = useManagerName(params.rmId);
    const data = report.data;

    return (
        <div className="flex min-w-0 flex-col gap-(--layout-row-gap)">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                    <WindowControl days={params.days} onDaysChange={onDaysChange} />
                    <ManagerFilter rmId={params.rmId} onManagerChange={onManagerChange} />
                </div>
                {data ? (
                    <span className="text-muted-foreground text-xs">
                        {windowHint(data, manager)}
                    </span>
                ) : null}
            </div>

            {report.isPending ? (
                <div
                    aria-busy="true"
                    aria-label="Loading the report"
                    className="flex flex-col gap-(--layout-row-gap)"
                >
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
                        {Array.from({ length: 5 }, (_, i) => (
                            <Skeleton key={i} className="h-[84px] rounded-lg" />
                        ))}
                    </div>
                    <div className="grid gap-(--layout-row-gap) lg:grid-cols-3">
                        {Array.from({ length: 3 }, (_, i) => (
                            <Skeleton key={i} className="h-[240px] rounded-md" />
                        ))}
                    </div>
                    <Skeleton className="h-[320px] rounded-md" />
                </div>
            ) : report.isError && !data ? (
                <ReportFailure copy={COPY.failed} onRetry={() => void report.refetch()} />
            ) : data ? (
                <>
                    <AppUsageFigures report={data} />
                    <div className="grid min-w-0 gap-(--layout-row-gap) lg:grid-cols-3">
                        <VersionsCard report={data} />
                        <DevicesCard report={data} />
                        <WorthACallCard report={data} />
                    </div>
                    <PeopleCard report={data} />
                    <SessionLogCard report={data} params={params} />
                </>
            ) : null}
        </div>
    );
}

/**
 * The five figures, two to a row on a phone, three on a tablet and five on a desk. The ticket's
 * sixth, sessions expired, is not recorded by the app, so it is not shown (Franka, 27 Oct 2026).
 */
export function AppUsageFigures({ report }: { report: AppUsageReport }) {
    const f = report.figures;
    const copy = COPY.figures;
    const latest = latestVersion(report.latestReleases) ?? copy.onLatest.theLatest;
    // Without a release list nobody can be on the latest, and 0% would say they all are behind.
    const listed = report.latestReleases.length > 0;
    return (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
            <Tile
                label={copy.opened.label}
                value={copy.opened.value(f.opened, f.appLogins)}
                note={copy.opened.note}
            />
            <Tile
                label={copy.signIns.label}
                value={String(f.signIns)}
                note={
                    f.signInsPerPerson === null
                        ? copy.signIns.nobody
                        : copy.signIns.note(f.signInsPerPerson)
                }
            />
            <Tile
                label={copy.onLatest.label}
                value={f.onLatestShare === null || !listed ? copy.empty : `${f.onLatestShare}%`}
                note={
                    listed
                        ? copy.onLatest.note(f.onLatest, f.opened, latest)
                        : copy.onLatest.noReleases
                }
            />
            <Tile
                label={copy.wellBehind.label}
                value={String(f.wellBehind)}
                note={firstNames(f.wellBehindNames)}
            />
            <Tile
                label={copy.resets.label}
                value={String(f.passwordResets)}
                note={f.passwordResets > 0 ? copy.resets.note : copy.none}
            />
        </div>
    );
}
